import { defineStore } from "pinia"
import { BaseUser } from "@vitable/asclepius-api-client-ts"

export type PendingPhoneClaim = {
  /** Masked challenged number (`***1234`); null when arming for a missing phone. */
  destinationHint: string | null
}

/**
 * The D7 phone-claim gate (AuthRevamp): a member must hold a verified,
 * sole-owned phone. Armed post-login when the member has NO phone on file
 * (mandatory number entry, no choices) or when this login was routed
 * through the shared-phone challenge and the member holds the challenged
 * number. In-memory only — never persisted (a full reload re-derives the
 * missing-phone case from the member; a contested claim re-arms on the
 * next challenged login).
 */
export const usePhoneClaimGateStore = defineStore("phoneClaimGateStore", {
  state: () => ({
    pendingClaim: null as PendingPhoneClaim | null,
    challengedHint: null as string | null
  }),
  getters: {
    isPending: (state): boolean => state.pendingClaim !== null
  },
  actions: {
    /** Called at challenge time (pre-auth) when the challenge demands a claim. */
    recordChallengedHint(destinationHint: string) {
      this.challengedHint = destinationHint
    },

    /** Arms or clears the gate from the freshly authenticated user. */
    evaluateForUser(user: BaseUser) {
      if (!user.phone) {
        this.pendingClaim = { destinationHint: null }
        return
      }

      // The challenge hint ends with the challenged number's last 4; a
      // genuine holder has that number on file. A leftover hint from an
      // abandoned login (or a mistyped number) must not gate this member.
      const hint = this.challengedHint
      if (hint && hint.length >= 4 && user.phone.endsWith(hint.slice(-4))) {
        this.pendingClaim = { destinationHint: hint }
        return
      }

      this.challengedHint = null
      this.pendingClaim = null
    },

    resolve() {
      this.pendingClaim = null
      this.challengedHint = null
    }
  }
})
